import { useMemo } from "react";
import { useField } from "formik";
import { Entity } from "@common/types/entity";
import { TransferRuleDirection } from "@common/types/transactionRule";
import { useEntitiesById } from "@hooks/useEntities";
import {
    FormRuleConditionType,
    FormRuleActionType,
    TransactionRuleConditionFormSchema,
    TransactionRuleActionFormSchema,
} from "./constants";

export interface CategoryVisibility {
    showBusiness: boolean;
    showPersonal: boolean;
}

type CategoryVisibilityContext = "conditions" | "actions";

export function getCategoryVisibility({
    entityIds,
    entityActionEntityId,
    entitiesById,
    context,
}: {
    entityIds: number[];
    entityActionEntityId: number | undefined;
    entitiesById: Map<number, Entity>;
    context: CategoryVisibilityContext;
}): CategoryVisibility {
    if (context === "actions") {
        const entityActionEntity = entityActionEntityId
            ? entitiesById.get(entityActionEntityId)
            : undefined;

        if (entityActionEntity) {
            return {
                showBusiness: !entityActionEntity.isPersonal,
                showPersonal: !!entityActionEntity.isPersonal,
            };
        }
    }

    const hasPersonal = entityIds.some(
        (id) => entitiesById.get(id)?.isPersonal,
    );
    const hasBusiness = entityIds.some(
        (id) => entitiesById.get(id)?.isBusiness,
    );

    return {
        showBusiness: !hasPersonal || hasBusiness,
        showPersonal: !hasBusiness || hasPersonal,
    };
}

export function useEntityCategoryVisibility(
    transferDirection?: TransferRuleDirection,
    context: CategoryVisibilityContext = "conditions",
): CategoryVisibility {
    const entitiesById = useEntitiesById({ excludeMockEntities: true });

    const conditionsFieldName = transferDirection
        ? `conditions.${transferDirection}`
        : "conditions";
    const actionsFieldName = transferDirection
        ? `actions.${transferDirection}`
        : "actions";
    const [conditions] =
        useField<TransactionRuleConditionFormSchema[]>(conditionsFieldName);
    const [actions] =
        useField<TransactionRuleActionFormSchema[]>(actionsFieldName);

    return useMemo(() => {
        const entityCondition = conditions.value.find(
            (condition) =>
                condition.conditionType === FormRuleConditionType.ENTITY,
        );
        const entityAction = actions.value?.find(
            (action) => action.actionType === FormRuleActionType.ENTITY,
        );

        return getCategoryVisibility({
            entityIds: entityCondition?.entityValue ?? [],
            entityActionEntityId: entityAction?.value?.entityId,
            entitiesById,
            context,
        });
    }, [conditions.value, actions.value, entitiesById, context]);
}
